import Editor from '@wandb/common/components/Monaco/Editor';
import {backendHost} from '@wandb/common/config';
import {TargetBlank} from '@wandb/common/util/links';
import {isNotNullOrUndefined} from '@wandb/common/util/types';
import * as _ from 'lodash';
import {editor, IDisposable, languages, Position} from 'monaco-editor';
import React, {useEffect, useMemo, useState} from 'react';
import {ExecutionResult} from 'react-apollo';
import {useHistory} from 'react-router-dom';
import {Button, Dropdown, DropdownItemProps, Modal} from 'semantic-ui-react';

import {
  CreateRunQueueMutation,
  CreateRunQueueMutationVariables,
  RunQueueAccessType,
  useCreateRunQueueMutation,
  useEntityArtifactsQuery,
  useFetchRunQueuesFromProjectQuery,
  useProjectPageQuery,
  usePushToRunQueueMutation,
  useRunArtifactVersionsQuery,
  useRunInfoAndConfigQuery,
  useUserProjectsQuery,
} from '../generated/graphql';
import {useViewer} from '../state/viewer/hooks';
import {projectRunQueue, run} from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import {getProjectsFromUserProjectsQuery} from './CreateReportModal';
import * as S from './EditLaunchConfigModal.styles';
import {
  ArtifactInfos,
  autoPopulateConfigResources,
  cleanConfig,
  getCompletionValues,
  RESOURCE_CONFIGS,
  ResourceConfigKeys,
  swapArtifactObjects,
} from './LaunchConfigEditor';
import {DEFAULT_RUN_QUEUE_NAME} from './RunQueueTab';

interface EditLaunchConfigModalControlledProps {
  entityName: string;
  projectName: string;
  runName?: string;
  runQueueName?: string;
  onClose: () => void;
}

type EditLaunchConfigModalProps = EditLaunchConfigModalControlledProps & {
  readOnly: boolean;
};

export interface CreateRunQueueArgs {
  variables: CreateRunQueueMutationVariables;
}

export type LaunchDropdown = {
  key: string;
  label: string;
  options: DropdownItemProps[];
  value?: string;
  setValue: (v: string | ResourceConfigKeys) => void;
  placeholder?: string;
};

interface ProjectData {
  id: string;
  name: string;
  entity: {
    id: string;
    name: string;
  };
}
interface InputArtifactsNode {
  artifactType: {
    id: string;
    name: string;
    project: ProjectData;
  };
  id: string;
  digest: string;
  commitHash?: string | null;
  versionIndex?: number | null;
  artifactSequence: {
    id: string;
    name: string;
    project: ProjectData;
  };
  aliases: Array<{
    artifactCollectionName: string;
    alias: string;
  }>;
  usedBy: {
    totalCount: number;
  };
}

export async function createRunQueueByName(
  createRunQueue: (
    variables: CreateRunQueueArgs
  ) => Promise<ExecutionResult<CreateRunQueueMutation>>,
  pushQueue: string,
  targetEntity: string,
  targetProject: string
) {
  const {data} = await createRunQueue({
    variables: {
      entityName: targetEntity,
      projectName: targetProject,
      queueName: pushQueue,
      access:
        pushQueue === DEFAULT_RUN_QUEUE_NAME
          ? RunQueueAccessType.Project
          : RunQueueAccessType.User,
    },
  });
  if (!data?.createRunQueue?.success || data?.createRunQueue?.queueID == null) {
    return;
  }

  return data?.createRunQueue?.queueID;
}
const nodeToArtiInfo = (node: InputArtifactsNode) => ({
  name: `Artifact/${node.artifactSequence.name}:v${node.versionIndex}`,
  entity: node.artifactSequence.project.entity.name,
  project: node.artifactSequence.project.name,
  version: `v${node.versionIndex}`,
  id: node.id,
  sequenceName: node.artifactSequence.name,
});

interface UsedArtifactInfo {
  label: string;
  name: string;
  entity: string;
  project: string;
  version: string;
  id: string;
  sequenceName: string;
}

export const EditLaunchConfigModalControlled: React.FC<EditLaunchConfigModalControlledProps> =
  props => {
    const projectQuery = useProjectPageQuery({
      variables: {
        projectName: props.projectName,
        entityName: props.entityName,
      },
    });

    if (
      projectQuery.loading ||
      projectQuery.error ||
      projectQuery.data?.project?.readOnly == null
    ) {
      return <div>Loading...</div>;
    }
    const readOnly = projectQuery.data?.project?.readOnly;
    return <EditLaunchConfigModal {...props} readOnly={readOnly} />;
  };

export const renderLaunchDropdown = (d: LaunchDropdown) => (
  <S.DropdownDiv key={d.key}>
    <S.Label>{d.label}</S.Label>
    <Dropdown
      options={d.options}
      selection
      search
      value={d.value}
      placeholder={d.placeholder}
      onChange={(e, {value}) => {
        d.setValue(value as string);
      }}
    />
  </S.DropdownDiv>
);

const EditLaunchConfigModal: React.FC<EditLaunchConfigModalProps> = ({
  entityName,
  projectName,
  runName,
  runQueueName,
  readOnly,
  onClose,
}) => {
  const [createRunQueue] = useCreateRunQueueMutation();
  const [pushToRunQueue] = usePushToRunQueueMutation();
  const [launchConfig, setLaunchConfig] = useState<string | null>(null);
  const [pushQueue, setPushQueue] = useState<string>(
    runQueueName ?? DEFAULT_RUN_QUEUE_NAME
  );

  const resourceOptions = [
    {
      key: 'local-container',
      text: 'Local Container',
      value: 'local-container',
    },
    {
      key: 'local-process',
      text: 'Local Process',
      value: 'local-process',
    },
    {
      key: 'kubernetes',
      text: 'Kubernetes',
      value: 'kubernetes',
    },
    {
      key: 'sagemaker',
      text: 'Amazon SageMaker',
      value: 'sagemaker',
    },
    {
      key: 'gcp-vertex',
      text: 'GCP Vertex',
      value: 'gcp-vertex',
    },
  ];

  const [resource, setResource] = useState<ResourceConfigKeys>(
    resourceOptions[0].value as ResourceConfigKeys
  );
  const [pushing, setPushing] = useState(false);
  const [targetProject, setTargetProject] = useState<string | undefined>(
    projectName
  );
  const [completionDisposable, setCompletionDisposable] =
    useState<IDisposable | null>(null);

  const viewer = useViewer();
  const history = useHistory();
  const [targetEntity, setTargetEntity] = useState<string>(
    readOnly ? viewer?.username! : entityName
  );
  const [editedConfig, setEditedConfig] = useState(false);
  const [editedTargetEntity, setEditedTargetEntity] = useState(false);
  const [editedTargetProject, setEditedTargetProject] = useState(false);
  const [editedPushQueue, setEditedPushQueue] = useState(false);
  const isSourcedFromRun = runName != null;

  const projectQuery = useProjectPageQuery({
    variables: {
      entityName: targetEntity,
      projectName: targetProject ?? 'uncategorized',
    },
    skip: targetProject == null,
  });

  const runInfoAndConfig = useRunInfoAndConfigQuery({
    variables: {
      projectName,
      entityName,
      runName: runName ?? '',
    },
    skip: !isSourcedFromRun,
  });

  const disposeProvider = (provider: IDisposable) => {
    provider.dispose();
    setCompletionDisposable(null);
  };

  const onModalClose = () => {
    if (completionDisposable != null) {
      disposeProvider(completionDisposable);
    }

    onClose();
  };

  const entityArtifactsQuery = useEntityArtifactsQuery({
    variables: {entityName: targetEntity},
  });

  const namesAndAliases: {[key: string]: ArtifactInfos} = useMemo(() => {
    const namesAndAliasesCollector: {[key: string]: ArtifactInfos} = {};
    if (entityArtifactsQuery.data == null) {
      return {};
    }
    entityArtifactsQuery.data.entity?.projects?.edges.forEach(projectEdge => {
      const artifactProjectName = projectEdge.node?.name;
      if (artifactProjectName == null) {
        return;
      }
      projectEdge.node?.artifactTypes.edges.forEach(artifactTypeEdge => {
        artifactTypeEdge.node?.artifactSequences?.edges.forEach(
          artifactSequenceEdge => {
            const artifactSequenceName = artifactSequenceEdge.node?.name;
            artifactSequenceEdge.node?.artifacts.edges.forEach(
              artifactsEdge => {
                const artifactId = artifactsEdge.node.id;
                artifactsEdge.node.aliases.forEach(alias => {
                  const aliasName = `${artifactSequenceName}:${alias.alias}`;
                  const aliasLabel = `Artifact/${aliasName}`;
                  namesAndAliasesCollector[aliasLabel] = {
                    label: aliasLabel,
                    name: aliasName,
                    project: artifactProjectName,
                    entity: targetEntity,
                    version: artifactId,
                  };
                });
                const versionName = `${artifactSequenceName}:v${artifactsEdge.node.versionIndex}`;
                const versionLabel = `Artifact/${versionName}`;
                namesAndAliasesCollector[versionLabel] = {
                  label: versionLabel,
                  name: versionName,
                  project: artifactProjectName,
                  entity: targetEntity,
                  version: artifactId,
                };
              }
            );
          }
        );
      });
    });
    return namesAndAliasesCollector;
  }, [entityArtifactsQuery, targetEntity]);

  const runArtifacts = useRunArtifactVersionsQuery({
    variables: {
      entityName,
      projectName,
      runName: runName ?? '',
      includeOutput: false,
      includeInput: true,
      pageSize: 100,
    },
    fetchPolicy: 'cache-and-network',
    skip: !isSourcedFromRun,
  });
  const usedArtisInfo: UsedArtifactInfo[] = useMemo(() => {
    return _.flatten(
      runArtifacts.data?.project?.run?.inputArtifacts?.edges.map(e => {
        if (e.usedAs.length > 0) {
          return e.usedAs.map(ua => {
            if (e.node != null) {
              return {
                label: ua,
                ...nodeToArtiInfo(e.node),
              };
            }
            return null;
          });
        } else if (e.node != null) {
          return {
            label: `${e.node.artifactSequence.name}:v${e.node.versionIndex}`,
            ...nodeToArtiInfo(e.node),
          };
        }
        return null;
      })
    ).filter(isNotNullOrUndefined);
  }, [runArtifacts.data]);

  const usedArtisMap = useMemo(() => {
    if (usedArtisInfo != null) {
      const mapped = usedArtisInfo.map(ai => ({[ai.label]: ai.name}));
      return Object.assign({}, ...mapped);
    } else {
      return {};
    }
  }, [usedArtisInfo]);
  const editLaunchConfig = (val: string) => {
    if (!editedConfig) {
      setEditedConfig(true);
      window.analytics?.track('In launch config editor, edited config', {
        entity: viewer?.entity,
        sourceEntity: entityName,
        sourceProject: projectName,
      });
    }
    setLaunchConfig(val);
  };

  const changeTargetEntity = (newTargetEntity: string) => {
    if (!editedTargetEntity) {
      setEditedTargetEntity(true);
      window.analytics?.track('In launch config editor, edited target entity', {
        entity: viewer?.entity,
        sourceEntity: entityName,
        targetEntity: newTargetEntity,
      });
    }
    setTargetEntity(newTargetEntity);
  };

  const changeTargetProject = (newTargetProject: string) => {
    if (!editedTargetProject) {
      setEditedTargetProject(true);
      window.analytics?.track(
        'In launch config editor, edited target project',
        {
          entity: viewer?.entity,
          sourceEntity: entityName,
          sourceProject: projectName,
          targetProject: newTargetProject,
        }
      );
    }
    setTargetProject(newTargetProject);
  };

  const changeResource = (newResource: string) => {
    if (!Object.keys(RESOURCE_CONFIGS).includes(newResource)) {
      console.error('Invalid resource selection');
    }
    setResource(newResource as ResourceConfigKeys);
    const resourceConfigString = autoPopulateConfigResources(
      newResource as ResourceConfigKeys
    );
    const lastBrace = launchConfig != null ? launchConfig.lastIndexOf('}') : -1;
    if (
      lastBrace > 0 &&
      launchConfig != null &&
      resourceConfigString.length > 0
    ) {
      const newConfig =
        launchConfig.substring(0, lastBrace - 1) +
        ',' +
        resourceConfigString.substring(1, resourceConfigString.length - 1) +
        '}';
      setLaunchConfig(newConfig);
    }
  };

  const changePushQueue = (queueName: string) => {
    if (!editedPushQueue) {
      setEditedPushQueue(true);
      window.analytics?.track('In launch config editor, edited target queue', {
        entity: viewer?.entity,
        sourceEntity: entityName,
        sourceProject: projectName,
        queueName,
      });
    }
    setPushQueue(queueName);
  };

  const userProjectsQueryResult = useUserProjectsQuery({
    variables: {
      userName: viewer?.username ?? '',
      includeReports: true,
    },
    fetchPolicy: 'cache-and-network',
    skip: viewer == null,
  });

  const projects = useMemo(() => {
    return getProjectsFromUserProjectsQuery(userProjectsQueryResult);
  }, [userProjectsQueryResult]);

  useEffect(() => {
    if (
      isSourcedFromRun &&
      (runInfoAndConfig.loading ||
        runInfoAndConfig.error ||
        runArtifacts.loading ||
        runArtifacts.error)
    ) {
      return;
    }

    let runConfig: any = {};
    if (isSourcedFromRun) {
      try {
        runConfig = JSON.parse(runInfoAndConfig.data?.project?.run?.config);
      } catch {
        console.log('failed to fetch config or config malformed');
      }
    }

    const filteredRunConfig = _.omit(cleanConfig(runConfig), '_wandb');
    const swappedRunConfig = swapArtifactObjects(filteredRunConfig);

    let shownLaunchConfig: any = {
      overrides: {
        ...((!isSourcedFromRun ||
          runInfoAndConfig.data?.project?.run?.runInfo?.args != null) && {
          args: runInfoAndConfig.data?.project?.run?.runInfo?.args ?? [],
        }),
        run_config: swappedRunConfig ?? {},
        ...(!isSourcedFromRun && {
          entry_point: [],
        }),
      },
    };
    if (!isSourcedFromRun) {
      shownLaunchConfig = {
        ...shownLaunchConfig,
        docker: {
          docker_image: '',
        },
      };
    }
    setLaunchConfig(JSON.stringify(shownLaunchConfig, null, 2));
  }, [
    entityName,
    projectName,
    isSourcedFromRun,
    runInfoAndConfig,
    runArtifacts,
    usedArtisInfo,
    usedArtisMap,
  ]);

  const projectNameOptions = useMemo(
    () =>
      projects
        .filter(proj => proj.entityName === targetEntity)
        .map(proj => proj.name),
    [targetEntity, projects]
  );

  useEffect(() => {
    if (
      targetProject != null &&
      projectNameOptions.length > 0 &&
      !projectNameOptions.includes(targetProject)
    ) {
      setTargetProject(undefined);
    }
  }, [targetEntity, projectNameOptions, targetProject]);

  // reset the provided artifacts when the entity changes
  useEffect(() => {
    if (entityArtifactsQuery.loading) {
      if (completionDisposable != null) {
        disposeProvider(completionDisposable);
      }
    }
  }, [completionDisposable, entityArtifactsQuery.loading]);

  const useFetchRunQueuesFromProjectQueryResult =
    useFetchRunQueuesFromProjectQuery({
      variables: {
        entityName: targetEntity,
        projectName: targetProject ?? 'uncategorized',
      },
    });

  const pushQueueNameIdMap = Object.fromEntries(
    useFetchRunQueuesFromProjectQueryResult.data?.project?.runQueues?.map(
      runQueue => {
        return [runQueue.name, runQueue.id];
      }
    ) ?? []
  );

  const projectOptions = projectNameOptions.map(name => {
    return {
      key: name,
      text: name,
      value: name,
    };
  });

  const entityOptions =
    [...new Set(projects.map(proj => proj.entityName))].map(ent => {
      return {
        key: ent,
        text: ent,
        value: ent,
      };
    }) ?? [];

  const runQueueOptions = Object.keys(pushQueueNameIdMap).map(name => {
    return {
      key: name,
      text: name,
      value: name,
    };
  });

  // first option should be default
  if (pushQueueNameIdMap[DEFAULT_RUN_QUEUE_NAME] == null) {
    runQueueOptions.unshift({
      key: DEFAULT_RUN_QUEUE_NAME,
      text: DEFAULT_RUN_QUEUE_NAME,
      value: DEFAULT_RUN_QUEUE_NAME,
    });
  } else if (runQueueOptions[0].key !== DEFAULT_RUN_QUEUE_NAME) {
    const initialIndex = runQueueOptions.findIndex(
      option => option.key === DEFAULT_RUN_QUEUE_NAME
    );

    const defaultRunQueue = runQueueOptions.splice(initialIndex, 1)[0];
    runQueueOptions.splice(0, 0, defaultRunQueue);
  }

  const pushRunToQueue = async () => {
    window.analytics?.track('In launch config editor, clicked push run', {
      entity: viewer?.entity,
      targetEntity,
      targetProject,
    });
    if (
      launchConfig == null ||
      pushing ||
      (isSourcedFromRun && runInfoAndConfig.data == null) ||
      entityArtifactsQuery.data == null ||
      targetProject == null
    ) {
      return;
    }
    setPushing(true);
    let queueID: string | undefined = pushQueueNameIdMap[pushQueue];
    if (queueID == null) {
      queueID = await createRunQueueByName(
        createRunQueue,
        pushQueue,
        targetEntity,
        targetProject
      );
      if (queueID == null) {
        alert('Run Queue creation failed');
        setPushing(false);
        return;
      }
    }
    let parsedConfig = {};
    try {
      parsedConfig = JSON.parse(launchConfig);
    } catch {
      console.log('failed to parse launch config');
      alert(
        'Problem parsing launch config. Please check your JSON and try again.'
      );
      setPushing(false);
      return;
    }

    const pushedRunSpec = JSON.stringify({
      ...parsedConfig,
      // runName should never be null in the following uri, since isSourcedFromRun = runName != null
      ...(isSourcedFromRun && {
        uri:
          backendHost() + run({entityName, projectName, name: runName ?? ''}),
      }),
      project: targetProject,
      entity: targetEntity,
      resource,
    });

    await pushToRunQueue({
      variables: {
        queueID,
        runSpec: pushedRunSpec,
      },
    });

    if (completionDisposable != null) {
      disposeProvider(completionDisposable);
    }

    await projectQuery.refetch();

    if (!isSourcedFromRun) {
      onModalClose();
    }
    history.push(
      projectRunQueue({entityName: targetEntity, name: targetProject!})
    );
  };

  const launchDropDowns: LaunchDropdown[] = [
    {
      key: 'entityDropdown',
      label: 'Entity:',
      options: entityOptions,
      value: targetEntity,
      setValue: changeTargetEntity,
    },
    {
      key: 'projectDropdown',
      label: 'Project:',
      options: projectOptions,
      value: targetProject,
      setValue: changeTargetProject,
      placeholder: 'Select project',
    },
    {
      key: 'runQueueDropdown',
      label: 'Queue:',
      options: runQueueOptions,
      value: pushQueue,
      setValue: changePushQueue,
    },
    {
      key: 'resourceDropdown',
      label: 'Resource:',
      options: resourceOptions,
      value: resource,
      setValue: changeResource,
    },
  ];

  return (
    <Modal open={true} className="edit-run-config-modal" onClose={onModalClose}>
      <Modal.Header>Modify your launch config</Modal.Header>
      <Modal.Content>
        <div className="editor-wrapper">
          {launchConfig == null ||
          (isSourcedFromRun && runInfoAndConfig.data == null) ||
          // the editor needs to remount everytime this query is made
          // to load the new entities artifacts
          entityArtifactsQuery.data == null ? (
            <Loader name="edit-launch-config" />
          ) : (
            <Editor
              value={launchConfig}
              onChange={editLaunchConfig}
              height={500}
              language="json"
              theme="vs-dark"
              onMount={(monacoEditor: editor.IStandaloneCodeEditor) => {
                setCompletionDisposable(
                  languages.registerCompletionItemProvider('json', {
                    provideCompletionItems: (
                      model: editor.ITextModel,
                      position: Position
                    ) => {
                      const items = getCompletionValues(
                        namesAndAliases,
                        model,
                        position
                      );
                      return {suggestions: items};
                    },
                  })
                );
              }}
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <S.Dropdowns>{launchDropDowns.map(renderLaunchDropdown)}</S.Dropdowns>
        <S.ActionsBottom>
          <span>
            Check the{' '}
            <TargetBlank href="https://docs.wandb.ai/guides/launch">
              launch documentation
            </TargetBlank>{' '}
            to see what options are available for{' '}
            {resourceOptions.find(r => r.value === resource)?.text}.
          </span>
          <S.Buttons>
            <Button
              size="tiny"
              disabled={pushing}
              onClick={() => {
                window.analytics?.track(
                  'In launch config editor, clicked cancel',
                  {
                    entity: viewer?.entity,
                    sourceEntity: entityName,
                    sourceProject: projectName,
                  }
                );
                onModalClose();
              }}>
              Cancel
            </Button>
            <Button
              size="tiny"
              primary
              disabled={pushing || targetProject == null}
              loading={pushing}
              onClick={pushRunToQueue}>
              Push Run
            </Button>
          </S.Buttons>
        </S.ActionsBottom>
      </Modal.Actions>
    </Modal>
  );
};

export default EditLaunchConfigModal;
